import 'pixi.js/unsafe-eval';
import { Assets, Container, type Texture } from 'pixi.js';
import { PixiApp } from './app/PixiApp';
import { SceneManager } from './app/SceneManager';
import { AppContext } from './app/AppContext';
import { loadFonts } from './app/fonts';
import { loadAtlas } from './assets/AtlasLoader';
import { setBackgroundTexture, setUiIcons } from './ui/widgets';
import { setOverlayCanvas } from './ui/domOverlay';
import { installKeyboardDismissal } from './ui/keyboard';
import { GameClient } from './net/client';
import { MessageTag } from './net/messageTags';
import { GameName, RoomName } from './net/enums';
import { decodeIsReady, decodeWaitReady, decodeGameInfo } from './net/codec/matchmaking';
import { LoginScene } from './scenes/LoginScene';
import { RegisterScene } from './scenes/RegisterScene';
import { MainMenuScene } from './scenes/MainMenuScene';
import { CreateGameScene } from './scenes/CreateGameScene';
import { SearchGameScene } from './scenes/SearchGameScene';
import { ReadyScene } from './scenes/ReadyScene';
import { BlackjackTableScene } from './scenes/BlackjackTableScene';
import { HokmTableScene } from './scenes/HokmTableScene';
import { BackgammonTableScene } from './scenes/BackgammonTableScene';
import { ProfileScene } from './scenes/ProfileScene';
import { PaymentScene } from './scenes/PaymentScene';
import { TicketScene } from './scenes/TicketScene';
import { OnlineListScene } from './scenes/OnlineListScene';
import { ReconnectScene } from './scenes/ReconnectScene';
import { ReconnectController } from './app/ReconnectController';
import { initSound, playSfx } from './app/sound';
import { resolveVisualFixtureSlug, VisualFixtureTransport } from './visual/fixtureTransport';
import { showVisualFixture } from './visual/showFixture';
import './app.css';
import {
  authenticateTelegram,
  hasTelegramLaunchData,
  initTelegram,
  installIosCanvasTouchGuard,
  isIosDevice,
  isLocalDevelopment,
  localDevelopmentClientId,
  setTelegramClosingConfirmation,
  shouldEnforceIosOnly,
} from './platform/telegram';

// WS endpoint: VITE_WS_URL (baked in at build time for Cloudflare/production) wins; otherwise
// same-origin /ws, which Vite proxies to the gateway in dev and Nginx proxies in prod.
const localDevelopment = isLocalDevelopment(location.hostname, import.meta.env.DEV);
const visualFixtureSlug = resolveVisualFixtureSlug(
  location.search,
  location.hostname,
  import.meta.env.DEV,
);
const configuredWsUrl =
  import.meta.env.VITE_WS_URL ||
  `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
const wsEndpoint = new URL(configuredWsUrl, location.href);
if (localDevelopment) {
  wsEndpoint.searchParams.set('client', localDevelopmentClientId(location.search));
}
const wsUrl = wsEndpoint.toString();

const PRELOAD_RELOAD_KEY = 'pardis:preload-reload';

/**
 * Vite emits this event when a cached entry bundle references a chunk that no longer exists after a
 * deployment. Reload once with a cache-busting query so Telegram's WebView fetches the current HTML.
 */
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();

  try {
    if (sessionStorage.getItem(PRELOAD_RELOAD_KEY) === '1') return;
    sessionStorage.setItem(PRELOAD_RELOAD_KEY, '1');
  } catch {
    /* Private browsing may deny sessionStorage; a cache-busted navigation is still safe. */
  }

  const freshUrl = new URL(window.location.href);
  freshUrl.searchParams.set('_refresh', Date.now().toString(36));
  window.location.replace(freshUrl);
});

// Once the new entry bundle remains healthy, permit recovery from a separate future deployment.
window.setTimeout(() => {
  try {
    sessionStorage.removeItem(PRELOAD_RELOAD_KEY);
  } catch {
    /* storage unavailable */
  }
}, 15_000);

function markPreviewBuild(): void {
  if (import.meta.env.VITE_DEPLOY_CHANNEL !== 'preview') return;

  document.documentElement.dataset.deployChannel = 'preview';
  document.title = `[TEST] ${document.title}`;

  const revision = import.meta.env.VITE_COMMIT_SHA?.slice(0, 7);
  const marker = document.createElement('div');
  marker.className = 'preview-build-marker';
  marker.textContent = revision ? `نسخه آزمایشی · ${revision}` : 'نسخه آزمایشی';
  marker.setAttribute('aria-label', revision ? `Test build ${revision}` : 'Test build');
  document.body.appendChild(marker);
}

async function boot(): Promise<void> {
  markPreviewBuild();
  if (!visualFixtureSlug) initTelegram();
  installKeyboardDismissal();
  const mount = document.getElementById('app');
  if (!mount) throw new Error('missing #app mount');

  // This release is intentionally iOS-only. Stop before Telegram authentication, asset loading, or
  // opening a gateway connection so Android and desktop launches cannot start a game session.
  if (!visualFixtureSlug && shouldEnforceIosOnly(window.location.hostname) && !isIosDevice()) {
    showIosOnly(mount);
    return;
  }

  // Local Vite builds use the loopback gateway's development identity so dev-multi can run two
  // independent clients. Every hosted build still requires signed Telegram launch data and a cookie.
  if (!visualFixtureSlug && !localDevelopment) {
    if (!hasTelegramLaunchData()) {
      showTelegramOnly(mount);
      return;
    }

    try {
      await authenticateTelegram(wsUrl);
    } catch {
      showTelegramOnly(mount, true);
      return;
    }
  }

  await loadFonts();

  const pixi = new PixiApp();
  await pixi.init(mount);
  const canvas = pixi.app.canvas as HTMLCanvasElement;
  setOverlayCanvas(canvas);
  installIosCanvasTouchGuard(canvas);

  const scenesRoot = new Container();
  const uiLayer = new Container();
  pixi.world.addChild(scenesRoot, uiLayer);

  const [profileAtlas, respackAtlas, bgTexture] = await Promise.all([
    loadAtlas('/atlas/profile.png', '/atlas/profile.tpsheet'),
    loadAtlas('/atlas/respack.png', '/atlas/respack.tpsheet').catch(() => null),
    Assets.load<Texture>('/img/background.jpg').catch(() => null),
  ]);
  setBackgroundTexture(bgTexture);

  // Standalone Unity UI icons (best-effort; a missing one just renders as an empty slot).
  const iconNames = ['coin', 'blackjack', 'hokm', 'backgammon', 'telegram', 'support', 'search', 'return', 'refresh-page-option', 'menu-burger', 'trophy', 'clock', 'cards', 'logo', 'user', 'users', 'wallet', 'lock', 'email', 'phone', 'key', 'ticket', 'enter', 'save', 'camera', 'bank', 'credit-card', 'refresh', 'play', 'eye', 'system-announcer-v2'];
  const loadedIcons = await Promise.all(iconNames.map((n) => Assets.load<Texture>(`/img/${n}.png`).catch(() => null)));
  const icons = new Map<string, Texture>();
  iconNames.forEach((n, i) => {
    const tex = loadedIcons[i];
    if (tex) icons.set(n, tex);
  });
  setUiIcons(icons);

  const fixtureTransport = visualFixtureSlug ? new VisualFixtureTransport(visualFixtureSlug) : null;
  const client = new GameClient(fixtureTransport ?? undefined);
  const scenes = new SceneManager(scenesRoot, () => pixi.relayout());
  const ctx = new AppContext(client, scenes, profileAtlas, respackAtlas, uiLayer, icons);

  // Matchmaking alerts can arrive before a table scene exists. Initialize the shared Web Audio player
  // now so the user's login/create/join taps can unlock it before IsReady / WaitReady is received.
  initSound();

  ctx.routes = {
    [RoomName.Login]: () => new LoginScene(ctx),
    [RoomName.Register]: () => new RegisterScene(ctx),
    [RoomName.MainMenu]: () => new MainMenuScene(ctx),
    [RoomName.NewGame]: () => new CreateGameScene(ctx),
    [RoomName.CreateHokm]: () => new CreateGameScene(ctx, GameName.Hokm),
    [RoomName.CreateBackgammon]: () => new CreateGameScene(ctx, GameName.Backgammon),
    [RoomName.SearchGame]: () => new SearchGameScene(ctx),
    [RoomName.Profile]: () => new ProfileScene(ctx),
    [RoomName.Payment]: () => new PaymentScene(ctx),
    [RoomName.Ticket]: () => new TicketScene(ctx),
    [RoomName.OnlineList]: () => new OnlineListScene(ctx),
    [RoomName.Connect]: () => new ReconnectScene(ctx),
  };

  ctx.installGlobalHandlers();

  scenes.onChange((scene) => {
    const activeGame = scene instanceof ReadyScene ||
      scene instanceof BlackjackTableScene ||
      scene instanceof HokmTableScene ||
      scene instanceof BackgammonTableScene;
    setTelegramClosingConfirmation(activeGame);
  });

  // Detect dropped connections and drive the reconnect page/modal (self-registers client.onClose).
  ctx.reconnect = new ReconnectController(ctx, wsUrl);

  // Ready/table transitions carry a decoded payload, so they're wired here (where scenes are imported).
  client.on(MessageTag.IsReady, (r) => {
    playSfx('ready');
    ctx.hideTelegramBackButton();
    scenes.show(new ReadyScene(ctx, decodeIsReady(r), true));
  });
  client.on(MessageTag.WaitReady, (r) => {
    playSfx('ready');
    ctx.hideTelegramBackButton();
    scenes.show(new ReadyScene(ctx, decodeWaitReady(r), false));
  });
  client.on(MessageTag.GameInfo, (r) => {
    ctx.hideTelegramBackButton();
    const seat = ctx.resolveTableSeat(decodeGameInfo(r));
    const table =
      seat.game.gameName === GameName.Hokm
        ? new HokmTableScene(ctx, seat)
        : seat.game.gameName === GameName.Backgammon
          ? new BackgammonTableScene(ctx, seat)
          : new BlackjackTableScene(ctx, seat);
    scenes.show(table);
  });

  if (visualFixtureSlug && fixtureTransport) {
    await client.connect('visual-fixture://loopback');
    ctx.hideTelegramBackButton();
    await showVisualFixture(visualFixtureSlug, fixtureTransport, ctx, scenes);
    return;
  }

  // Publish the URL and start the bounded handshake before entering Login, but do not block rendering
  // on network availability. LoginScene observes the eventual open and sends LoadLogin exactly once.
  const initialConnection = client.connect(wsUrl);
  ctx.hideTelegramBackButton();
  scenes.show(new LoginScene(ctx));
  requestAnimationFrame(() => document.getElementById('boot-shell')?.remove());
  void initialConnection.catch(() => undefined); // Login/activation retries on demand.
}

function showTelegramOnly(mount: HTMLElement, authenticationFailed = false): void {
  mount.replaceChildren();
  const panel = document.createElement('main');
  panel.className = 'telegram-only';

  const logo = document.createElement('img');
  logo.src = '/img/telegram.png';
  logo.alt = '';

  const heading = document.createElement('h1');
  heading.textContent = 'باز کردن در تلگرام';

  const detail = document.createElement('p');
  detail.textContent = authenticationFailed
    ? 'ورود تلگرام تأیید نشد. لطفاً مینی‌اپ را ببندید و دوباره از داخل تلگرام باز کنید.'
    : 'این بازی فقط از طریق مینی‌اپ رسمی تلگرام قابل اجرا است.';

  const english = document.createElement('p');
  english.className = 'telegram-only__english';
  english.textContent = authenticationFailed
    ? 'Telegram authentication failed. Close and reopen the Mini App in Telegram.'
    : 'Open in Telegram';

  panel.append(logo, heading, detail, english);
  mount.append(panel);
}

function showIosOnly(mount: HTMLElement): void {
  mount.replaceChildren();
  const panel = document.createElement('main');
  panel.className = 'telegram-only ios-only';

  const badge = document.createElement('div');
  badge.className = 'ios-only__badge';
  badge.textContent = 'iOS';
  badge.setAttribute('aria-hidden', 'true');

  const heading = document.createElement('h1');
  heading.textContent = 'نسخه مخصوص آیفون';

  const detail = document.createElement('p');
  detail.textContent =
    'این نسخه از بازی فقط روی آیفون و آیپد قابل اجرا است. لطفاً مینی‌اپ را با تلگرام در دستگاه iOS باز کنید.';

  const english = document.createElement('p');
  english.className = 'telegram-only__english';
  english.textContent = 'This version is available only on iOS.';

  panel.append(badge, heading, detail, english);
  mount.append(panel);
}

boot().catch((err: unknown) => {
  // Surface boot failures in the page instead of a blank canvas.
  // eslint-disable-next-line no-console
  console.error('[boot] failed', err);
  const mount = document.getElementById('app');
  if (mount) {
    const detail = err instanceof Error ? (err.stack ?? err.message) : String(err);
    mount.innerHTML =
      `<pre style="color:#fca5a5;white-space:pre-wrap;padding:24px;` +
      `font:14px/1.5 monospace;direction:ltr">خطا در بارگذاری بازی / Boot error:\n\n${detail}</pre>`;
  }
});
